/**
 * This file provides filters configured for the Table component.
 * Each filter type encapsulates rendering and filtering behavior. Filters can be further configured to have specific "tiers" of width or to hide their labels (which is, at least for now, a common pattern on Dashboards pages).
 */
import { type JSX } from 'react';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { ColorTheme, GroupOptionType, GroupedOptions, OptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import { CellValue } from '../Table';
import styles from '../Table.module.css';

type FilterWidth = 'standard' | 'large';
const FILTER_WIDTH_DEFAULT: FilterWidth = 'standard';

/**
 * Available filter types. Each type determines the filter's UI component and filtering logic.
 */
export enum FilterType {
    /**
     * Allows filtering by one or more selections across multiple properties of a row object.
     * Uses AND logic between groups and OR logic within groups.
     */
    MULTIPLE_PROPERTIES,
    /**
     * Allows filtering by a single property of a row object.
     * Can be configured to allow or disallow multiple selections.
     */
    SINGLE_PROPERTY,
    /** Filters a row by tag IDs listed in a property of the row object. */
    TAGS,
    /** Filters a row by user IDs listed in a property of the row object. */
    USERS,
}

/**
 * Allows filtering by one or more selections across multiple properties of a row object.
 * Uses AND logic between groups and OR logic within groups.
 *
 * @typeParam RowData - The shape of each row's data object.
 */
export interface MultiPropertyFilterConfig<RowData> {
    type: FilterType.MULTIPLE_PROPERTIES;
    id: string;
    groups: {
        label: string;
        getFilterableDataForRow: (row: RowData) => CellValue[];
        options: { name: string; value: CellValue }[];
        defaultSelections?: CellValue[];
    }[];
    label: string;

    disabled?: boolean;
    /** When true, hides the label visually but uses it as an aria-label for accessibility. This is, at least for now, a common pattern on Dashboards pages. */
    hideLabel?: boolean;
    width?: FilterWidth;
}

/**
 * Allows filtering by one or more selections for a single property of the row object (OR logic).
 *
 * @typeParam RowData - The shape of each row's data object.
 */
export interface SinglePropertyFilterConfig<RowData> {
    type: FilterType.SINGLE_PROPERTY;

    /**
     * Calculates the value(s) of the property of a row.
     * Must return an array to support rows that match multiple values (e.g., a row with multiple statuses). Thus, commonly returns an array of a single value.
     */
    getFilterableDataForRow: (row: RowData) => CellValue[];
    id: string;
    label: string;
    options: { name: string; value: CellValue }[];

    /** When true, allows selecting multiple options. Defaults to false. */
    canSelectMultiple?: boolean;
    defaultSelections?: CellValue[];
    disabled?: boolean;
    /** When true, hides the label visually but uses it as an aria-label for accessibility. This is, at least for now, a common pattern on Dashboards pages. */
    hideLabel?: boolean;
    preventClearing?: boolean;
    width?: FilterWidth;
}

/**
 * Allows filtering for row objects that have at least one of the selected tags assigned.
 * Options are automatically populated from `cachedData`.
 *
 * @typeParam RowData - The shape of each row's data object.
 */
export interface TagFilterConfig<RowData> {
    type: FilterType.TAGS;

    /** Calculates the tag IDs for the row object (OR logic). */
    getTagIdsForRow: (row: RowData) => string[];
    id: string;
    label: string;

    defaultSelections?: string[];
    disabled?: boolean;
    /** When true, hides the label visually but uses it as an aria-label for accessibility. This is, at least for now, a common pattern on Dashboards pages. */
    hideLabel?: boolean;
    width?: FilterWidth;
}

/**
 * Allows filtering for row objects that have at least one of the selected users assigned. Useful for filtering by owner, reviewer, delegates, etc.
 * Options are automatically populated from `cachedData`.
 *
 * @typeParam RowData - The shape of each row's data object.
 */
export interface UserFilterConfig<RowData> {
    type: FilterType.USERS;
    label: string;
    /** Calculates the users assigned to the row object (OR logic). */
    getUserIdsForRow: (row: RowData) => string[];

    defaultSelections?: string[];
    disabled?: boolean;
    /** When true, hides the label visually but uses it as an aria-label for accessibility. This is, at least for now, a common pattern on Dashboards pages. */
    hideLabel?: boolean;
    id: string;
    /** When true, includes inactive users in the dropdown. Defaults to false. */
    includeInactive?: boolean;
    /** Restricts the dropdown to users with the given roles. If omitted, shows all users. */
    rolesRestriction?: Role[];
    width?: FilterWidth;
}

export type FilterConfig<RowData> = MultiPropertyFilterConfig<RowData> | SinglePropertyFilterConfig<RowData> | TagFilterConfig<RowData> | UserFilterConfig<RowData>;

interface FilterProps<RowData> {
    filter: FilterConfig<RowData>;
    /** Current selected value(s). The type depends on filter type. Typing could probably be improved if our wildly inconsistent form field components were improved. */
    selectedValue: any;
    onValueChange: (value: any) => void;
    colorTheme: ColorTheme;
}

/**
 * Renders a filter control based on its type and configuration.
 * Do NOT use this when defining a table. This is only to be used internally by Table.
 *
 * @typeParam RowData - The shape of each row's data object.
 */
export const Filter = <RowData,>(props: FilterProps<RowData>): JSX.Element => {
    const disabled = props.filter.disabled ?? false;
    const widthClass: string = (() => {
        const filterWidth = props.filter.width ?? FILTER_WIDTH_DEFAULT;
        switch (filterWidth) {
            case 'large':
                return styles.filterLargeWidth;
            case 'standard':
                return styles.filterStandardWidth;
        }
    })();

    switch (props.filter.type) {
        case FilterType.SINGLE_PROPERTY:
            return <SinglePropertyFilter filter={props.filter} selectedValue={props.selectedValue} onValueChange={props.onValueChange} colorTheme={props.colorTheme} widthClass={widthClass} disabled={disabled} />;
        case FilterType.MULTIPLE_PROPERTIES:
            return <MultiPropertyFilter filter={props.filter} selectedValues={props.selectedValue} onValueChange={props.onValueChange} colorTheme={props.colorTheme} widthClass={widthClass} disabled={disabled} />;
        case FilterType.USERS:
            return <UserFilter filter={props.filter} selectedUserIds={props.selectedValue} onValueChange={props.onValueChange} colorTheme={props.colorTheme} widthClass={widthClass} disabled={disabled} />;
        case FilterType.TAGS:
            return <TagFilter filter={props.filter} defaultSelectedTags={props.selectedValue} onValueChange={props.onValueChange} colorTheme={props.colorTheme} widthClass={widthClass} disabled={disabled} />;
    }
};

interface SinglePropertyFilterProps<RowData> {
    colorTheme: ColorTheme;
    disabled: boolean;
    filter: SinglePropertyFilterConfig<RowData>;
    onValueChange: (value: CellValue | CellValue[] | undefined) => void;
    selectedValue: CellValue | CellValue[] | undefined;
    widthClass: string;
}

const SinglePropertyFilter = <RowData,>(props: SinglePropertyFilterProps<RowData>): JSX.Element => {
    const formattedOptions: OptionType[] = props.filter.options.map((opt) => ({
        label: opt.name,
        value: opt.value,
    }));

    const handleChange = (value: ChangeEventType): void => {
        props.onValueChange(value as CellValue | CellValue[] | undefined);
    };

    return (
        <div className={props.widthClass}>
            <FormFieldSelect formFieldId={props.filter.id} accessibilityLabel={props.filter.hideLabel ? props.filter.label : undefined} formFieldLabel={props.filter.hideLabel ? undefined : props.filter.label} options={formattedOptions} selectedOption={props.selectedValue} handleChange={handleChange} colorTheme={props.colorTheme} selectMultiple={props.filter.canSelectMultiple} notClearable={props.filter.preventClearing} disabled={props.disabled} />
        </div>
    );
};

interface MultiPropertyFilterProps<RowData> {
    colorTheme: ColorTheme;
    disabled: boolean;
    filter: MultiPropertyFilterConfig<RowData>;
    onValueChange: (value: GroupOptionType[]) => void;
    selectedValues: GroupOptionType[] | undefined;
    widthClass: string;
}

const MultiPropertyFilter = <RowData,>(props: MultiPropertyFilterProps<RowData>): JSX.Element => {
    const handleChange = (value: GroupOptionType[]): void => {
        props.onValueChange(value);
    };

    const groupedOptions: GroupedOptions[] = props.filter.groups.map((group, groupIndex) => ({
        label: group.label,
        options: group.options.map((opt) => ({
            groupId: groupIndex,
            label: opt.name,
            value: opt.value,
        })),
    }));

    const defaultSelectedOptions: GroupOptionType[] = props.filter.groups.flatMap((group, groupIndex) =>
        (group.defaultSelections ?? [])
            .map((value) => {
                const option = group.options.find((opt) => opt.value === value);
                return option
                    ? {
                          groupId: groupIndex,
                          label: option.name,
                          value: option.value,
                      }
                    : null;
            })
            .filter((opt) => opt !== null)
    );

    return (
        <div className={props.widthClass}>
            <FormFieldMultiOptionSelect formFieldId={props.filter.id} colorTheme={props.colorTheme} defaultSelectedOptions={defaultSelectedOptions} options={groupedOptions} accessibilityLabel={props.filter.hideLabel ? props.filter.label : undefined} formFieldLabel={props.filter.hideLabel ? undefined : props.filter.label} handleChange={handleChange} disabled={props.disabled} />
        </div>
    );
};

interface UserFilterProps<RowData> {
    colorTheme: ColorTheme;
    disabled: boolean;
    filter: UserFilterConfig<RowData>;
    onValueChange: (value: string[]) => void;
    selectedUserIds: string[] | undefined;
    widthClass: string;
}

const UserFilter = <RowData,>(props: UserFilterProps<RowData>): JSX.Element => {
    const cachedData = useCachedData();

    const selectedUsers = props.selectedUserIds?.map((userId) => cachedData.users.find((user) => user.cognito_subject === userId)).filter((user): user is UserResponse => user !== undefined);

    const handleUsersSelected = (users: UserResponse[] | undefined): void => {
        const userIds = users?.map((user) => user.cognito_subject) ?? [];
        props.onValueChange(userIds);
    };

    return (
        <div className={props.widthClass}>
            <FormFieldUserMultiSelect formFieldId={props.filter.id} accessibilityLabel={props.filter.hideLabel ? props.filter.label : undefined} formFieldLabel={props.filter.hideLabel ? undefined : props.filter.label} selectedUsers={selectedUsers} onUsersSelected={handleUsersSelected} rolesRestriction={props.filter.rolesRestriction} includeInactive={props.filter.includeInactive} colorTheme={props.colorTheme} disabled={props.disabled} />
        </div>
    );
};

interface TagFilterProps<RowData> {
    defaultSelectedTags: string[] | undefined;
    filter: TagFilterConfig<RowData>;
    onValueChange: (value: string[]) => void;
    colorTheme: ColorTheme;
    widthClass: string;
    disabled: boolean;
}

const TagFilter = <RowData,>(props: TagFilterProps<RowData>): JSX.Element => {
    const cachedData = useCachedData();
    const tagOptions = cachedData.getTagCategoriesGroupedOptions();

    const defaultSelectedOptions = tagOptions.flatMap((group) => group.options).filter((option) => props.defaultSelectedTags?.includes(option.value as string));

    const handleChange = (value: GroupOptionType[]): void => {
        const tagIds = value.map((opt) => opt.value as string);
        props.onValueChange(tagIds);
    };

    return (
        <div className={props.widthClass}>
            <FormFieldMultiOptionSelect formFieldId={props.filter.id} colorTheme={props.colorTheme} defaultSelectedOptions={defaultSelectedOptions} options={tagOptions} accessibilityLabel={props.filter.hideLabel ? props.filter.label : undefined} formFieldLabel={props.filter.hideLabel ? undefined : props.filter.label} handleChange={handleChange} disabled={props.disabled} />
        </div>
    );
};
